import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import "card-pattern/dist/main.css";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import Modal from "../../../components/common/Modal";
import Loader from "../../../components/common/Loader";
import {
  CostCenter,
  fetchUBTTeam,
  updateUBTTeam,
  addCostCenterAPI,
  setUbtName,
  setDateTeamEstablished,
  setTeamRating,
  setServiceType,
  setUsingHuddles,
  setDefaultMembership,
  deleteCostCenterAPI,
  setEditableTeam,
  setEditingTeam,
  setCreateTeam,
  setCostCenterList,
  resetTeamProfile,
} from "../../../store/slices/costCenterSlice";

import { TableRow } from "../../../components/common/Table/Table";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { EditableTableRow } from "../../../components/common/EditableTable";
import ManageCostCenter from "./ManageCostCenter";
import CostCenterList from "./CostCenterList";
import TeamInfoForm from "./TeamInfoForm";
import { teamStorage } from "../../../utils/teamStorage";
import {
  convertDateToYYYYMMDD,
  formatCostCenter,
  parseFlexibleDateToMMDDYYYY,
} from "../../../utils/date";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import ErrorBanner, {
  ValidationError,
} from "../../../components/common/ErrorBanner";
import { canEditCostCenter, isAdminWithEditPrivileges } from "../../../utils/editPrivilegeCheck";

// Constants
const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 45% !important;
    width: 90% !important;
    top: 20% !important;
  }
`;

const BANNER_AUTO_HIDE_DELAY = 5000;

// TypeScript Types
interface BannerState {
  heading: string;
  messageInfo: string;
  variant?: "Error" | "Success" | "Informational" | "Alert";
}

interface TeamFormValues {
  ubtName: string;
  dateTeamEstablished: string;
  teamRating: string;
  serviceType: string;
  usingHuddles: string;
  defaultMembership: string;
}

interface APIResult {
  meta?: {
    requestStatus?: string;
  };
  payload?: unknown;
}

const FALLBACK_ADD_COST_CENTER_ERROR = "Something went wrong";

export const resolveAddCostCenterErrorMessage = (result: APIResult): string => {
  const payload = result?.payload;
  if (payload) {
    return payload as string;
  }
  return FALLBACK_ADD_COST_CENTER_ERROR;
};

const buildRefreshFailureBanner = (action: "added" | "deleted"): BannerState => ({
  heading: `Cost Center ${action}, but the table could not be refreshed`,
  messageInfo:
    "Please reload the page or try again to see the latest cost center list.",
  variant: "Error",
});

const createEmptyCostCenter = (): CostCenter => ({
  regionId: 0,
  glString: "",
  region: "",
  entity: "",
  area: "",
  center: "",
  location: "",
  costCenter: "",
  shift: "",
  noOfEmployees: "",
  entityId: 0,
  areaId: 0,
  centerId: 0,
  locationId: 0,
  costCenterId: 0,
  shiftId: 0,
});

export const createPrefilledCostCenterFromLatest = (
  latestCostCenter: CostCenter | undefined,
): CostCenter => {
  const empty = createEmptyCostCenter();
  if (!latestCostCenter) return empty;

  return {
    ...empty,
    entity: latestCostCenter.entity ?? "",
    entityCode: latestCostCenter.entityCode,
    entityId: latestCostCenter.entityId ?? 0,
  };
};

const shouldClearEditingCostCenter = (
  editingCostCenter: CostCenter | null,
  removedCostCenterId: CostCenter["costCenterId"],
  removedGlString: CostCenter["glString"],
): boolean => {
  if (!editingCostCenter) {
    return false;
  }

  if (removedCostCenterId) {
    return editingCostCenter.costCenterId === removedCostCenterId;
  }

  return editingCostCenter.glString === removedGlString;
};

/** Builds the API-ready cost center payload from a {@link CostCenter} form object. */
const buildCostCenterPayload = (
  costCenter: CostCenter,
  regionCodeFromEmployee: string | null,
): {
  costCenter: string;
  region: string;
  entity: string;
  area: string;
  location: string;
  shift: string;
} => ({
  costCenter: formatCostCenter(costCenter.costCenter),
  region:
    costCenter.regionCode ??
    regionCodeFromEmployee ??
    extractCodeFromLabel(costCenter.region),
  entity: costCenter.entityCode ?? extractCodeFromLabel(costCenter.entity),
  area: costCenter.areaCode ?? extractCodeFromLabel(costCenter.area),
  location: costCenter.locationCode ?? extractCodeFromLabel(costCenter.location),
  shift: costCenter.shift,
});

// Helper to extract code from label (text before first hyphen or space) - memoized utility
export const extractCodeFromLabel = (value: string | undefined): string => {
  if (!value) return "";
  const hyphenIndex = value.indexOf("-");
  const spaceIndex = value.indexOf(" ");

  if (hyphenIndex > 0 && (spaceIndex < 0 || hyphenIndex < spaceIndex)) {
    return value.substring(0, hyphenIndex).trim();
  }
  if (spaceIndex > 0) {
    return value.substring(0, spaceIndex).trim();
  }
  return value.trim();
};

// Helper to normalize various yes/no representations to 'yes'|'no' or empty string
const normalizeYesNo = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "y" || s === "yes") return "yes";
    if (s === "n" || s === "no") return "no";
    return s;
  }
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") return value === 1 ? "yes" : "no";
  return "";
};

// Parse a rating string like "3 Team in Place" -> 3, default to 3
const parseRating = (ratingStr: string): number => {
  const match = /^(\d+)/.exec(ratingStr || "");
  return match ? Number.parseInt(match[1], 10) : 3;
};

// Helper to format display value for Yes/No fields
const formatYesNoDisplay = (value: unknown, isEditMode: boolean): string => {
  if (isEditMode) {
    return normalizeYesNo(value);
  }
  if (value === undefined || value === null) {
    return "";
  }
  // Only convert primitives to string (string, number, boolean)
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    const strValue = String(value);
    if (strValue.trim() === "") {
      return "";
    }
    const lowerValue = strValue.toLowerCase();
    return lowerValue === "yes" || lowerValue === "y" ? "Yes" : "No";
  }
  return "";
};

// Helper to safely scroll to top (no-op in jsdom/tests)
export const safeScrollToTop = () => {
  if (globalThis.window?.scrollTo === undefined) return;
  if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent))
    return;

  try {
    globalThis.window.scrollTo({ top: 0, behavior: "smooth" });
  } catch {
    // Fallback to instant scroll if smooth not supported
    try {
      globalThis.window.scrollTo(0, 0);
    } catch {
      // Silent fail - scrollTo not supported
    }
  }
};

const AddUBTProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMountedRef = useRef(true);
  const costCenterTableRef = useRef<HTMLDivElement>(null);
  const costCenterFormRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const passedTeamId = location.state?.teamID ?? null;
  const passedNuid =
    location.state?.nuid ?? localStorage.getItem("userId") ?? "";

  // Get Redux state
  const {
    teamId: teamIdState,
    ubtName,
    dateTeamEstablished,
    teamRating,
    serviceType,
    usingHuddles,
    defaultMembership,
    costCenterList,
    editableTeam,
    editingTeam,
    createTeam,
    loading,
  } = useAppSelector((state) => state.costCenter);

  // Employee info (used for default region code)
  const regionCodeFromEmployee = useAppSelector(
    (state) => state.employee?.employeeDetails?.regionCode ?? null,
  );

  // Profile info (used for edit/delete privilege checks)
  const profile = useAppSelector((state) => state.profile?.profile);

  // Local state for UI-only concerns
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<CostCenter | null>(null);
  const [showAddCostCenterForm, setShowAddCostCenterForm] =
    useState<boolean>(false);
  const [showUBTCostCenterList, setShowUBTCostCenterList] =
    useState<boolean>(false);
  const [editingCostCenter, setEditingCostCenter] = useState<CostCenter | null>(
    null,
  );
  const [isAddCostCenter, setIsAddCostCenter] = useState<boolean>(false);
  const [isUpdateCostCenter, setIsUpdateCostCenter] = useState<boolean>(false);
  const [banner, setBanner] = useState<BannerState | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ValidationError[] | null
  >(null);
  const [showDefaultMembershipModal, setShowDefaultMembershipModal] =
    useState<boolean>(false);
  const [costCenterTableKey, setCostCenterTableKey] = useState<number>(0);
  const [defaultMembershipModalTitle, setDefaultMembershipModalTitle] =
    useState<string>("Update Default Membership");
  const [defaultMembershipModalMessage, setDefaultMembershipModalMessage] =
    useState<string>("");
  const [pendingMembershipRows, setPendingMembershipRows] = useState<
    EditableTableRow[] | null
  >(null);
  const [teamInfoFormKey, setTeamInfoFormKey] = useState<number>(0);
  const [previousValues, setPreviousValues] = useState<TeamFormValues>({
    ubtName: "",
    dateTeamEstablished: "",
    teamRating: "",
    serviceType: "",
    usingHuddles: "",
    defaultMembership: "",
  });
  // Derived state - show Add Cost Center button when cost centers exist, form is not shown, and team is not being edited
  const showAddCostCenterButton = useMemo(
    () => costCenterList.length > 0 && !showAddCostCenterForm && !editingTeam,
    [costCenterList.length, showAddCostCenterForm, editingTeam],
  );



  const scrollSectionIntoView = useCallback(
    (ref: React.RefObject<HTMLDivElement>) => {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    },
    [],
  );

  // Auto-hide banner after delay, scroll to banner, and move focus to it when shown
  useEffect(() => {
    if (!banner) return;
    // Scroll to and focus the banner so screen readers and keyboard users are notified
    setTimeout(() => {
      if (bannerRef.current) {
        bannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        bannerRef.current.focus();
      }
    }, 50);
    const id = setTimeout(() => setBanner(null), BANNER_AUTO_HIDE_DELAY);
    return () => clearTimeout(id);
  }, [banner]);

  // Note: Validation errors are now cleared selectively by context (team vs cost center)
  // using clearTeamValidationErrors() and clearCostCenterValidationErrors() helpers
  // This allows team and cost center validation errors to coexist properly

  // Memoize display values for Yes/No fields to prevent recalculation
  const displayUsingHuddles = useMemo(
    () => formatYesNoDisplay(usingHuddles, editingTeam || createTeam),
    [usingHuddles, editingTeam, createTeam],
  );

  const displayDefaultMembership = useMemo(
    () => formatYesNoDisplay(defaultMembership, editingTeam || createTeam),
    [defaultMembership, editingTeam, createTeam],
  );

  // Update UI state when cost center list changes
  useEffect(() => {
    if (costCenterList.length > 0) {
      setShowUBTCostCenterList(true);
    }
  }, [costCenterList.length]);

  const refreshCostCenterTable = useCallback(async (): Promise<boolean> => {
    // Clear the current rows before reloading so the table cannot briefly render stale data.
    dispatch(setCostCenterList([]));
    setShowUBTCostCenterList(false);
    setCostCenterTableKey((prev) => prev + 1);

    const refreshResult: APIResult = await dispatch(
      fetchUBTTeam({ teamId: teamIdState, nuid: passedNuid }),
    );

    if (!isMountedRef.current) {
      return false;
    }

    if (refreshResult?.meta?.requestStatus !== "fulfilled") {
      return false;
    }

    setCostCenterTableKey((prev) => prev + 1);
    return true;
  }, [dispatch, teamIdState, passedNuid]);

  const navigate = useNavigate();
  // Ref to persist banner intent across navigation
  const pendingBannerRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // If a teamID was passed via navigation state, fetch team details via store thunk
  useEffect(() => {
    if (!passedTeamId) return;

    dispatch(fetchUBTTeam({ teamId: passedTeamId, nuid: passedNuid }));
  }, [passedTeamId, passedNuid, dispatch]);

  // Reset to create mode when navigating without a teamID
  useEffect(() => {
    if (!passedTeamId) {
      dispatch(resetTeamProfile());
      dispatch(setCreateTeam(true));
      setShowUBTCostCenterList(false);
      setShowAddCostCenterForm(false);
      setEditingCostCenter(null);
      setValidationErrors(null);
      // Show success banner if coming from Save and Create New
      if (location.state?.showSuccessBanner || pendingBannerRef.current) {
        setBanner({
          heading: "Cost Center Added Successfully",
          messageInfo: "",
          variant: "Success",
        });
        pendingBannerRef.current = false;
        // Remove the flag so it doesn't persist on further navigation
        navigate(location.pathname, { replace: true, state: {} });
      }
      // Don't clear success banners immediately - let them display for a moment
      // Only clear on actual navigation away (not on same-page resets)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedTeamId, location.key]);

  // Determine whether the user can edit/delete any cost center, used to control
  // edit button visibility in both the team info table and cost center table.
  // When the cost center list is empty (new team), any admin with edit privileges
  // can still edit the team info.
  const showActionColumns = useMemo(
    () => {
      if (profile == null || !isAdminWithEditPrivileges(profile)) return false;
      if (costCenterList.length === 0) return true;
      return costCenterList.some((row: CostCenter) =>
        canEditCostCenter(profile, {
          regionCode: row.regionCode ?? "",
          areaCode: row.areaCode ?? "",
          centerCode: row.centerCode ?? "",
          locationCode: row.locationCode ?? "",
        }),
      );
    },
    [costCenterList, profile],
  );

  // Derive the region to show in the Add Cost Center form.
  // If every cost center on the team shares the same regionCode, that region is
  // authoritative and we use it so dropdown options load for the correct region.
  // When the list is empty or regions differ, fall back to the profile's region.
  const { resolvedRegionCode, resolvedRegionDescription } = useMemo(() => {
    if (costCenterList.length > 0) {
      const regionMap = new Map<string, string>(
        costCenterList.map((cc: CostCenter) => [cc.regionCode ?? "", cc.region ?? ""])
      );
      if (regionMap.size === 1) {
        const [[code, description]] = [...regionMap.entries()];
        return { resolvedRegionCode: code, resolvedRegionDescription: description };
      }
    }
    return {
      resolvedRegionCode: profile?.currentRegion ?? "",
      resolvedRegionDescription: profile?.regionDescription ?? "",
    };
  }, [costCenterList, profile]);

  // Memoize editable row to prevent unnecessary re-renders and resets
  const editableRowMapped: EditableTableRow[] = useMemo(
    () => [
      {
        id:
          teamIdState && String(teamIdState).trim() !== "" ? teamIdState : "--",
        nameOfUBT:
          createTeam || editingTeam ? (
            (ubtName ?? "")
          ) : (
            <div className="max-w-[125px] whitespace-normal break-words overflow-wrap-[anywhere]">
              {ubtName ?? ""}
            </div>
          ),
        dateTeamEstablished: dateTeamEstablished,
        teamRating: teamRating,
        serviceType: serviceType,
        usingHuddles: displayUsingHuddles,
        defaultMembership: displayDefaultMembership,
        selectAction: showActionColumns ? (
          <button
            type="button"
            className={`button flex items-center group hover:bg-[#003B71]! hover:text-white! m-0! w-full! desktop:w-auto! self-stretch justify-center box-border max-w-none${
              editingTeam
                ? " bg-[#003B71]! text-white! [&>img]:brightness-0! [&>img]:invert!"
                : " -light"
            }`}
            onClick={() => handleEditTeam({ id: "", nameOfUBT: ubtName })}
            data-analytics-type="button"
            data-analytics-click="Default"
          >
            <img
              src="/assets/images/pencil-outline.svg"
              alt="Edit icon"
              className="h-6! w-6! group-hover:brightness-0! group-hover:invert!"
            />
            <span className="flex items-center justify-center w-full">
              {editingTeam ? "Editing" : "Edit"}
            </span>
          </button>
        ) : null,
      },
    ],
    [
      teamIdState,
      ubtName,
      dateTeamEstablished,
      teamRating,
      serviceType,
      displayUsingHuddles,
      displayDefaultMembership,
      createTeam,
      editingTeam,
      showActionColumns,
    ],
  );

  // Memoize cost center rows to prevent unnecessary recalculations
  const costCenterRow: TableRow[] = useMemo(
    () =>
      costCenterList.map((row: CostCenter) => {
        const isActive =
          showAddCostCenterForm &&
          row.costCenterId === editingCostCenter?.costCenterId &&
          row.glString === editingCostCenter.glString &&
          row.entity === editingCostCenter.entity &&
          row.area === editingCostCenter.area &&
          row.center === editingCostCenter.center &&
          row.location === editingCostCenter.location &&
          row.costCenter === editingCostCenter.costCenter &&
          row.shift === editingCostCenter.shift;

        // Check if the user has edit privilege for this specific cost center row
        const hasEditAccess = canEditCostCenter(profile, {
          regionCode: row.regionCode ?? "",
          areaCode: row.areaCode ?? "",
          centerCode: row.centerCode ?? "",
          locationCode: row.locationCode ?? "",
        });

        return {
          rowKey: row.costCenterId ? String(row.costCenterId) : row.glString,
          glString: row.glString ?? "",
          entity: row.entity ?? "",
          location: row.location ?? "",
          costCenter: row.costCenter ?? "",
          shift: row.shift ?? "All",
          noOfEmployees: row.noOfEmployees ?? "0",
          editAction: hasEditAccess ? (
            <button
              type="button"
              className={`button flex items-center group hover:bg-[#003B71]! hover:text-white! m-0! w-full! desktop:w-auto! self-stretch justify-center box-border max-w-none ${
                isActive
                  ? " bg-[#003B71]! text-white! [&>img]:brightness-0! [&>img]:invert!"
                  : " -light"
              }`}
              onClick={() => handleSelectedCostCenter(row)}
            >
              <img
                src="/assets/images/pencil-outline.svg"
                alt="Edit icon"
                className="h-6! w-6! group-hover:brightness-0! group-hover:invert!"
              />
              <span className="flex items-center justify-center w-full">
                {isActive ? "Editing" : "Edit"}
              </span>
            </button>
          ) : null,
          deleteAction:
            hasEditAccess && costCenterList.length > 1 ? (
              <button
                type="button"
                onClick={() => handleDeleteClick(row)}
                className="flex items-center justify-center m-0! w-full! desktop:w-auto! h-10! p-0 cursor-pointer"
                aria-label="Delete"
              >
                <img
                  src="/assets/images/trash-solid.svg"
                  alt="Delete icon"
                  className="h-6! w-6!"
                />
              </button>
            ) : null,
        };
      }),
    [costCenterList, showAddCostCenterForm, editingCostCenter, profile],
  );

  // Ensure create-team flag is off while editing an existing team.
  useEffect(() => {
    if (!passedTeamId) return;
    if (editableTeam || editingTeam) {
      dispatch(setCreateTeam(false));
    }
  }, [editableTeam, editingTeam, passedTeamId, dispatch]);

  const handleDeleteClick = useCallback((_selectedRow: CostCenter) => {
    setDeleteTarget(_selectedRow);
    setShowDeleteModal(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) {
      setShowDeleteModal(false);
      return;
    }

    const payload = buildCostCenterPayload(deleteTarget, regionCodeFromEmployee);

    setShowDeleteModal(false);

    try {
      const result: APIResult = await dispatch(
        deleteCostCenterAPI({
          teamId: teamIdState,
          payload,
          costCenterId: deleteTarget.costCenterId,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        const removedCostCenterId = deleteTarget.costCenterId;
        const removedGlString = deleteTarget.glString;

        if (
          shouldClearEditingCostCenter(
            editingCostCenter,
            removedCostCenterId,
            removedGlString,
          )
        ) {
          setEditingCostCenter(null);
          setShowAddCostCenterForm(false);
        }

        const didRefresh = await refreshCostCenterTable();
        if (didRefresh) {
          setBanner({
            heading: "Cost Center Deleted Successfully",
            messageInfo: "",
            variant: "Success",
          });
        } else {
          setBanner(buildRefreshFailureBanner("deleted"));
        }
      } else {
        if (!isMountedRef.current) return;
        setBanner({
          heading: "An Error Occurred in deleting this cost center",
          messageInfo:
            "We were unable to complete your request. Please try again later.",
          variant: "Error",
        });
      }
    } catch {
      if (!isMountedRef.current) return;
      setBanner({
        heading: "An Error Occurred in deleting this cost center",
        messageInfo:
          "We were unable to complete your request. Please try again later.",
        variant: "Error",
      });
    } finally {
      if (isMountedRef.current) {
        setDeleteTarget(null);
      }
    }
  }, [
    dispatch,
    teamIdState,
    deleteTarget,
    regionCodeFromEmployee,
    passedNuid,
    editingCostCenter,
    refreshCostCenterTable,
  ]);

  // Helper function to validate a team name field
  const validateTeamName = useCallback(
    (name: string): ValidationError | null => {

      if (!name || name.trim() === "") {
        return { field: "nameOfUBT", message: "Team Name Required." };
      }
      if (name.trim().length < 3) {
        return { field: "nameOfUBT", message: "Minimum 3 characters required." };
      }
      return null;
    },
    [],
  );

  // Helper function to validate a date field.
  // Accepts flexible input formats (MM-DD-YYYY, MM/DD/YYYY, M-D-YY, M/D/YY, etc.).
  // 2-digit years are expanded to 20YY. Year must not exceed 2099.
  const validateDateField = useCallback(
    (date: string): ValidationError | null => {
      if (!date || date.trim() === "") {
        return {
          field: "dateTeamEstablished",
          message: "Date Team Established Required.",
        };
      }

      const normalized = parseFlexibleDateToMMDDYYYY(date);
      if (normalized === null) {
        return {
          field: "dateTeamEstablished",
          message: "Please enter a valid date (MM-DD-YYYY).",
        };
      }

      return null;
    },
    [],
  );

  const handleEditTeam = useCallback(
    (_selectedRow: { id: string; nameOfUBT: string }) => {
      // Store current values before editing
      setPreviousValues({
        ubtName,
        dateTeamEstablished,
        teamRating,
        serviceType,
        usingHuddles,
        defaultMembership,
      });
      // Clear any existing validation errors when entering edit mode
      setValidationErrors(null);
      // Clear any banners
      setBanner(null);
      // Ensure we enter edit mode explicitly (don't toggle)
      dispatch(setEditableTeam(true));
      dispatch(setEditingTeam(true));
      dispatch(setCreateTeam(false));
      hideCostAddCostCenterFormList();
    },
    [
      ubtName,
      dateTeamEstablished,
      teamRating,
      serviceType,
      usingHuddles,
      defaultMembership,
      dispatch,
    ],
  );

  /** Validates the team form fields, delegating to the field-level helpers. */
  const validateTeamData = useCallback((): {
    isValid: boolean;
    errors: ValidationError[];
  } => {
    const errors: ValidationError[] = [];
    const nameError = validateTeamName(ubtName);
    if (nameError) errors.push(nameError);
    const dateError = validateDateField(dateTeamEstablished);
    if (dateError) errors.push(dateError);
    return { isValid: errors.length === 0, errors };
  }, [ubtName, dateTeamEstablished, validateTeamName, validateDateField]);

  const hideCostCenterForm = useCallback(() => {
    dispatch(setEditableTeam(true));
    dispatch(setEditingTeam(false));
    setShowUBTCostCenterList(true);
    setShowAddCostCenterForm(false);
  }, [dispatch]);

  const handleUpdateTeamCancel = useCallback(() => {
    // Restore previous values using Redux actions
    dispatch(setUbtName(previousValues.ubtName));
    dispatch(setDateTeamEstablished(previousValues.dateTeamEstablished));
    dispatch(setTeamRating(previousValues.teamRating));
    dispatch(setServiceType(previousValues.serviceType));
    dispatch(setUsingHuddles(previousValues.usingHuddles));
    dispatch(setDefaultMembership(previousValues.defaultMembership));
    // Clear validation errors when canceling
    setValidationErrors(null);
    // Clear any error banners
    setBanner(null);
    hideCostCenterForm();
  }, [previousValues, dispatch, hideCostCenterForm]);

  const handleCostCenterFormCancel = useCallback(() => {
    setEditingCostCenter(null);
    // Clear validation errors when canceling cost center form
    setValidationErrors(null);
    // Clear any error banners
    setBanner(null);
    hideCostCenterForm();
    scrollSectionIntoView(costCenterTableRef);
  }, [hideCostCenterForm, scrollSectionIntoView]);

  const handleCloseKeepBanner = useCallback(() => {
    // Scroll to top so the banner is visible, then close the form
    safeScrollToTop();
    setEditingCostCenter(null);
    hideCostCenterForm();
  }, [hideCostCenterForm, scrollSectionIntoView]);

  const hideCostAddCostCenterFormList = useCallback(() => {
    setShowAddCostCenterForm(false);
    setShowUBTCostCenterList(false);
  }, []);

  const showCostCenterForm = useCallback(() => {
    setShowAddCostCenterForm(true);
  }, []);

  const buildUpdatePayload = useCallback(() => {
    const effectiveUbtName = ubtName;
    const effectiveTeamRating = teamRating;
    const effectiveServiceType = serviceType;
    const effectiveUsingHuddles = normalizeYesNo(usingHuddles);
    const effectiveDefaultMembership = normalizeYesNo(defaultMembership);
    const effectiveDateTeamEstablished = dateTeamEstablished;

    return {
      teamName: effectiveUbtName?.trim(),
      shift: "All",
      usingHuddles: effectiveUsingHuddles === "yes" ? "Y" : "N",
      defaultMembership: effectiveDefaultMembership === "yes" ? "Y" : "N",
      rating: parseRating(effectiveTeamRating),
      serviceDeliveryType: effectiveServiceType,
      teamDateCreated: convertDateToYYYYMMDD(effectiveDateTeamEstablished),
    };
  }, [
    ubtName,
    teamRating,
    serviceType,
    usingHuddles,
    defaultMembership,
    dateTeamEstablished,
  ]);

  /** Runs validation and, on failure, shows errors and scrolls to top. Returns true when valid. */
  const applyValidationGuard = useCallback((): boolean => {
    const validation = validateTeamData();
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      safeScrollToTop();
      setBanner(null);
      return false;
    }
    return true;
  }, [validateTeamData, safeScrollToTop]);

  const handleUpdateTeam = useCallback(async () => {
    if (!applyValidationGuard()) return;

    const payload = buildUpdatePayload();

    try {
      await dispatch(updateUBTTeam({ teamId: teamIdState, payload })).unwrap();
      if (!isMountedRef.current) return;
      // Refresh data first, then show success banner (so loader doesn't hide it)
      await dispatch(fetchUBTTeam({ teamId: teamIdState, nuid: passedNuid }));
      if (!isMountedRef.current) return;
      setBanner({
        heading: "Team updated successfully!",
        messageInfo: "",
        variant: "Success",
      });
      hideCostCenterForm();
    } catch {
      if (!isMountedRef.current) return;
      // Error already handled by Redux; just update UI
      setBanner({
        heading: "An Error Occurred in updating this team",
        messageInfo:
          "We were unable to complete your request. Please try again later.",
        variant: "Error",
      });
    }
  }, [
    applyValidationGuard,
    buildUpdatePayload,
    dispatch,
    teamIdState,
    passedNuid,
    hideCostCenterForm,
  ]);

  const handleCreateCostCenterForm = useCallback(() => {
    if (!applyValidationGuard()) return;

    setValidationErrors(null);
    dispatch(setEditableTeam(true));
    const firstCostCenter = costCenterList[0];
    setEditingCostCenter(createPrefilledCostCenterFromLatest(firstCostCenter));
    setIsAddCostCenter(true);
    setIsUpdateCostCenter(false);
    setShowAddCostCenterForm(true);
    // Scroll to the cost center form so the user sees the form after clicking Add
    scrollSectionIntoView(costCenterFormRef);
  }, [applyValidationGuard, dispatch, costCenterList]);

  const handleActionClick = useCallback(
    (rowData: { id?: string | number; nuid?: string }) => {
      const id = rowData?.id ?? "";
      if (id) {
        teamStorage.setTeamId(id);
        navigate('/home/projects', {
          state: { teamId: id, nuid: rowData?.nuid ?? null },
        });
      }
    },
    [navigate],
  );

  const handleSaveAndCreateNew = useCallback(
    async (newCostCenter: CostCenter): Promise<boolean> => {
      const payload = buildCostCenterPayload(newCostCenter, regionCodeFromEmployee);

      try {
        const result = (await dispatch(
          addCostCenterAPI({ teamId: teamIdState, payload }),
        )) as APIResult;

        if (!isMountedRef.current) return false;

        if (result.meta?.requestStatus === "fulfilled") {
          // Set pending banner before navigation
          pendingBannerRef.current = true;
          // Refresh data from API, then close form, show banner, and scroll to top
          dispatch(fetchUBTTeam({ teamId: teamIdState, nuid: passedNuid })).then(() => {
            if (!isMountedRef.current) return;
            // Navigate to add-profile route with no state to guarantee add mode and a clean page
            navigate('/home/add-profile', { replace: true, state: {} });
          });
          return true;
        }

        setBanner({
          heading: "An Error Occurred in Adding this Cost Center",
          messageInfo: resolveAddCostCenterErrorMessage(result),
          variant: "Error",
        });
        return false;
      } catch {
        if (!isMountedRef.current) return false;
        setBanner({
          heading: "An Error Occurred in Adding this Cost Center",
          messageInfo: FALLBACK_ADD_COST_CENTER_ERROR,
          variant: "Error",
        });
        return false;
      }
    },
    [dispatch, teamIdState, regionCodeFromEmployee, passedNuid],
  );

  const handleSaveCostCenter = useCallback(
    async (newCostCenter: CostCenter): Promise<boolean> => {
      const payload = buildCostCenterPayload(newCostCenter, regionCodeFromEmployee);

      try {
        const result = (await dispatch(
          addCostCenterAPI({ teamId: teamIdState, payload }),
        )) as APIResult;

        if (!isMountedRef.current) return false;

        if (result.meta?.requestStatus === "fulfilled") {
          setShowUBTCostCenterList(false);
          const didRefresh = await refreshCostCenterTable();
          setBanner(
            didRefresh
              ? {
                  heading: "Cost Center Added Successfully",
                  messageInfo: "",
                  variant: "Success",
                }
              : buildRefreshFailureBanner("added"),
          );
          setEditingCostCenter(null);
          setShowAddCostCenterForm(false);
          scrollSectionIntoView(costCenterTableRef);
          return true;
        }

        setBanner({
          heading: "An Error Occurred in Adding this Cost Center",
          messageInfo: resolveAddCostCenterErrorMessage(result),
          variant: "Error",
        });
        return false;
      } catch {
        if (!isMountedRef.current) return false;
        setBanner({
          heading: "An Error Occurred in Adding this Cost Center",
          messageInfo: FALLBACK_ADD_COST_CENTER_ERROR,
          variant: "Error",
        });
        return false;
      }
    },
    [
      dispatch,
      teamIdState,
      passedNuid,
      regionCodeFromEmployee,
      refreshCostCenterTable,
      scrollSectionIntoView,
    ],
  );

  const handleUpdateCostCenter = useCallback(
    (_updatedCostCenter: CostCenter, didUpdate: boolean) => {
      // Only fetch if an actual update occurred
      if (didUpdate) {
        dispatch(fetchUBTTeam({ teamId: teamIdState, nuid: passedNuid }));
      }
      setEditingCostCenter(null);
      hideCostCenterForm();
      scrollSectionIntoView(costCenterTableRef);
    },
    [dispatch, teamIdState, passedNuid, hideCostCenterForm, scrollSectionIntoView],
  );

  const handleBreadcrumbPrevious = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const handleSelectedCostCenter = useCallback(
    (costCenter: CostCenter) => {
      setEditingCostCenter(costCenter);
      setIsUpdateCostCenter(true);
      setIsAddCostCenter(false);
      showCostCenterForm();
      scrollSectionIntoView(costCenterFormRef);
    },
    [showCostCenterForm, scrollSectionIntoView],
  );

  // Helper function to extract changed field values
  const extractChangedFields = useCallback((row: EditableTableRow) => {
    const changedFields: string[] = [];
    const fieldValues: Record<string, string> = {};

    if (row.nameOfUBT !== undefined) {
      fieldValues.nameOfUBT = (row.nameOfUBT as string) ?? "";
      changedFields.push("nameOfUBT");
    }
    if (row.dateTeamEstablished !== undefined) {
      fieldValues.dateTeamEstablished =
        (row.dateTeamEstablished as string) ?? "";
      changedFields.push("dateTeamEstablished");
    }
    if (row.teamRating !== undefined) {
      fieldValues.teamRating = (row.teamRating as string) ?? "";
      changedFields.push("teamRating");
    }
    if (row.serviceType !== undefined) {
      fieldValues.serviceType = (row.serviceType as string) ?? "";
      changedFields.push("serviceType");
    }
    if (row.usingHuddles !== undefined) {
      fieldValues.usingHuddles = (row.usingHuddles as string) ?? "";
      changedFields.push("usingHuddles");
    }
    if (row.defaultMembership !== undefined) {
      fieldValues.defaultMembership = (row.defaultMembership as string) ?? "";
      changedFields.push("defaultMembership");
    }

    return { changedFields, fieldValues };
  }, []);

  // Helper function to validate changed fields
  const validateChangedFields = useCallback(
    (
      changedFields: string[],
      fieldValues: Record<string, string>,
      currentErrors: ValidationError[] | null,
    ): ValidationError[] | null => {
      if (
        !currentErrors ||
        currentErrors.length === 0 ||
        changedFields.length === 0
      ) {
        return currentErrors;
      }

      // Remove errors for changed fields
      const remainingErrors = currentErrors.filter(
        (error) => !changedFields.includes(error.field),
      );

      // Re-validate changed fields
      const newErrors: ValidationError[] = [];

      if (changedFields.includes("nameOfUBT")) {
        const error = validateTeamName(fieldValues.nameOfUBT);
        // Remove error if 3 or more characters are entered
        if (!error || (fieldValues.nameOfUBT && fieldValues.nameOfUBT.trim().length >= 3)) {
          // No error if valid
        } else if (error) {
          newErrors.push(error);
        }
      }

      if (changedFields.includes("dateTeamEstablished")) {
        const error = validateDateField(fieldValues.dateTeamEstablished);
        if (error) newErrors.push(error);
      }

      // Combine remaining and new errors
      const allErrors = [...remainingErrors, ...newErrors];
      return allErrors.length > 0 ? allErrors : null;
    },
    [validateTeamName, validateDateField],
  );

  const dispatchFieldUpdates = useCallback(
    (row: EditableTableRow) => {
      if (row.nameOfUBT !== undefined) {
        dispatch(setUbtName((row.nameOfUBT as string) ?? ""));
      }
      if (row.dateTeamEstablished !== undefined) {
        const rawDate = (row.dateTeamEstablished as string) ?? "";
        // Normalize to MM-DD-YYYY so the Redux store and payload conversion
        // always work with a canonical format, regardless of how the user typed it.
        const normalizedDate = parseFlexibleDateToMMDDYYYY(rawDate) ?? rawDate;
        dispatch(setDateTeamEstablished(normalizedDate));
      }
      if (row.teamRating !== undefined) {
        dispatch(setTeamRating((row.teamRating as string) ?? ""));
      }
      if (row.serviceType !== undefined) {
        dispatch(setServiceType((row.serviceType as string) ?? ""));
      }
      if (row.usingHuddles !== undefined) {
        dispatch(setUsingHuddles((row.usingHuddles as string) ?? ""));
      }
      if (row.defaultMembership !== undefined) {
        dispatch(setDefaultMembership((row.defaultMembership as string) ?? ""));
      }
    },
    [dispatch],
  );

  const applyRowsChange = useCallback(
    (updatedRows: EditableTableRow[]) => {
      if (!isMountedRef.current || updatedRows.length === 0) return;

      const firstRow = updatedRows[0];
      const { changedFields, fieldValues } = extractChangedFields(firstRow);

      const updatedErrors = validateChangedFields(
        changedFields,
        fieldValues,
        validationErrors,
      );
      if (updatedErrors !== validationErrors) {
        setValidationErrors(updatedErrors);
      }

      dispatchFieldUpdates(firstRow);
    },
    [
      extractChangedFields,
      validateChangedFields,
      validationErrors,
      dispatchFieldUpdates,
    ],
  );

  const handleConfirmDefaultMembershipChange = useCallback(() => {
    if (pendingMembershipRows) {
      applyRowsChange(pendingMembershipRows);
    }
    setPendingMembershipRows(null);
    setDefaultMembershipModalTitle("Update Default Membership");
    setDefaultMembershipModalMessage("");
    setShowDefaultMembershipModal(false);
  }, [pendingMembershipRows, applyRowsChange]);

  const handleCancelDefaultMembershipChange = useCallback(() => {
    setPendingMembershipRows(null);
    setDefaultMembershipModalTitle("Update Default Membership");
    setDefaultMembershipModalMessage("");
    setShowDefaultMembershipModal(false);
    // Force TeamInfoForm remount so dropdown value snaps back to Redux state.
    setTeamInfoFormKey((prev) => prev + 1);
  }, []);

  const handleRowsChange = useCallback(
    (updatedRows: EditableTableRow[]) => {
      if (!isMountedRef.current || updatedRows.length === 0) return;

      const firstRow = updatedRows[0];

      if (editingTeam && firstRow.defaultMembership !== undefined) {
        const currentMembership = normalizeYesNo(defaultMembership);
        const nextMembership = normalizeYesNo(firstRow.defaultMembership);

        if (currentMembership === "yes" && nextMembership === "no") {
          setPendingMembershipRows(updatedRows);
          setDefaultMembershipModalTitle("Remove Defaulted Members?");
          setDefaultMembershipModalMessage(
            "Updating Default Membership from Yes to No will remove Defaulted Members from the Team",
          );
          setShowDefaultMembershipModal(true);
          return;
        }

        if (currentMembership === "no" && nextMembership === "yes") {
          setPendingMembershipRows(updatedRows);
          setDefaultMembershipModalTitle("Add Defaulted Members?");
          setDefaultMembershipModalMessage(
            "Updating Default Membership from No to Yes will add Defaulted Members to the Team",
          );
          setShowDefaultMembershipModal(true);
          return;
        }
      }

      applyRowsChange(updatedRows);
    },
    [editingTeam, defaultMembership, applyRowsChange],
  );

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      {loading && <Loader />}
      <div className="flex justify-between items-end">
        <div className="mb-6!">
        <BreadCrumbs
          previousPage="UBT Home"
          currentPage=""
          onPreviousClick={handleBreadcrumbPrevious}
        />
        </div>
        {teamIdState && String(teamIdState).trim() !== "" && (
          <div className="mobile-margin lg:mt-15! ">
            <div className="about-this-team" style={{ color: '#677083' }}> About this team :</div>
            <Link
              to={`/home/members`}
              data-analytics-type="link"
              state={{ from: "Add UBT Profile", teamId: teamIdState }}
              className="no-underline! p1 -medium"
            >
              Members
            </Link>
            <Link
              to={`/home/projects`}
              data-analytics-type="link"
              state={{ from: "Add UBT Profile", teamId: teamIdState }}
              className="no-underline! p1 -medium pl-6! "
            >
              Projects
            </Link>
            <Link
              to={`/home/history`}
              data-analytics-type="link"
              state={{ from: "Add UBT Profile", teamId: teamIdState }}
              className="no-underline! p1 -medium pl-6!"
            >
              History
            </Link>
          </div>
        )}
      </div>

      <div className="items-end mb-4! md:mb-0! desktop:pb-4! ml-4! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
        <div className="">
          <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">
            Add or edit a unit based team profile
          </h1>

          {/* Always render banner at the top for visibility */}
          {banner && (
            <div ref={bannerRef} tabIndex={-1} aria-live="polite">
              <CommonErrorBanner
                heading={banner.heading}
                messageInfo={banner.messageInfo}
                variant={banner.variant}
              />
            </div>
          )}
          {validationErrors && validationErrors.length > 0 && (
            <div className="mb-4! mt-14! w-[78%]">
              <ErrorBanner errors={validationErrors} />
            </div>
          )}

          <p className="h4 mt-8!">Add team Information</p>
          <p className="p3 -book mt-2 md:mb-4!">
            Note that a unit base team must have at least one cost center.
          </p>

          <TeamInfoForm
            key={teamInfoFormKey}
            createTeam={createTeam}
            editingTeam={editingTeam}
            editableRowMapped={editableRowMapped}
            onEditTeam={handleEditTeam}
            onRowsChange={handleRowsChange}
            onCreateCostCenterForm={handleCreateCostCenterForm}
            onUpdateTeamCancel={handleUpdateTeamCancel}
            onUpdateTeam={handleUpdateTeam}
            validationErrors={validationErrors}
            teamInfoLocked={showAddCostCenterForm}
          />
          <div className="mt-10!" ref={costCenterTableRef}>
            <CostCenterList
              key={costCenterTableKey}
              showUBTCostCenterList={showUBTCostCenterList}
              costCenterListLength={costCenterList.length}
              costCenterRow={costCenterRow}
              onActionClick={handleActionClick}
            />
            {showAddCostCenterButton && costCenterList.length > 0 && (
              <div className="flex gap-x-4 mt-8!">
                <button
                  className="button py-0! -inverted hover:text-white!"
                  data-testid="add-cost-center-btn"
                  data-analytics-type="button"
                  data-analytics-click="Default"
                  onClick={handleCreateCostCenterForm}
                >
                  Add Cost Center
                </button>
              </div>
            )}
          </div>
          {showAddCostCenterForm && editingCostCenter && (
            <div ref={costCenterFormRef}>
              <ManageCostCenter
                costCenter={editingCostCenter}
                onCancel={handleCostCenterFormCancel}
                onCloseKeepBanner={handleCloseKeepBanner}
                onSave={handleSaveCostCenter}
                onSaveAndCreateNew={handleSaveAndCreateNew}
                onUpdate={handleUpdateCostCenter}
                isAddCostCenter={isAddCostCenter}
                isUpdateCostCenter={isUpdateCostCenter}
                costCenterCount={costCenterList.length}
                setBanner={setBanner}
                onValidationErrors={setValidationErrors}
                validationErrors={validationErrors}
                resolvedRegionCode={resolvedRegionCode}
                resolvedRegionDescription={resolvedRegionDescription}
              />
            </div>
          )}
        </div>
      </div>
      <div className="mb-10!">
        <div className="-mb-27! tablet:hidden">
          <ScrollToTopBtn />
        </div>
      </div>
      <Modal
        isOpen={showDeleteModal}
        title="Delete Cost Center"
        clearBtn="Cancel"
        submitBtn="Delete"
        onSecondary={handleCancelDelete}
        onPrimary={handleConfirmDelete}
        onClose={handleCancelDelete}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-10 h-10 shrink-0"
          />
          <p className="p3 -book mb-4!">
            Do you want to delete this cost center from your UBT Profile page?
          </p>
        </div>
        <style>{MODAL_STYLES}</style>
      </Modal>
      <Modal
        isOpen={showDefaultMembershipModal}
        title={defaultMembershipModalTitle}
        clearBtn="Cancel"
        submitBtn="Continue"
        onSecondary={handleCancelDefaultMembershipChange}
        onPrimary={handleConfirmDefaultMembershipChange}
        onClose={handleCancelDefaultMembershipChange}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-10 h-10 shrink-0"
          />
          <p className="p3 -book mb-4!">{defaultMembershipModalMessage}</p>
        </div>
        <style>{MODAL_STYLES}</style>
      </Modal>
    </div>
  );
};

export default AddUBTProfile;
